/**
 * SessionRecordingControls Component v2.3
 * 
 * Recording controls for guided view sessions.
 * Only visible to super_admin and bank_partner roles.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useGuidedView } from '../../contexts/GuidedViewContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  createRecordingSession,
  addRecordingEvent,
  finalizeRecording,
  SESSION_EVENTS,
  SESSION_PURPOSES,
  formatDuration
} from '../../guided/sessionRecording';
import { Circle, Square, Save, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

export const SessionRecordingControls = ({ onSessionSaved }) => {
  const { user } = useAuth();
  const {
    isOpen,
    persona,
    mode,
    speed,
    currentStepIndex,
    isPlaying
  } = useGuidedView();

  const [isRecording, setIsRecording] = useState(false);
  const [purpose, setPurpose] = useState(SESSION_PURPOSES.GENERAL);
  const [recordingTime, setRecordingTime] = useState(0);
  const sessionRef = useRef(null);
  const timerRef = useRef(null);
  const prevStepRef = useRef(null);

  // Check if user can record
  const canRecord = ['super_admin', 'bank_partner'].includes(user?.role);

  // Track step changes
  useEffect(() => {
    if (!isRecording || !sessionRef.current) return;

    if (prevStepRef.current !== currentStepIndex) {
      // Record step exit
      if (prevStepRef.current !== null) {
        addRecordingEvent(sessionRef.current, SESSION_EVENTS.STEP_EXIT, {
          stepIndex: prevStepRef.current
        });
      }
      
      // Record step enter
      addRecordingEvent(sessionRef.current, SESSION_EVENTS.STEP_ENTER, {
        stepIndex: currentStepIndex
      });
      
      prevStepRef.current = currentStepIndex;
    }
  }, [isRecording, currentStepIndex]);

  // Track persona changes
  useEffect(() => {
    if (!isRecording || !sessionRef.current) return;
    
    addRecordingEvent(sessionRef.current, SESSION_EVENTS.PERSONA_CHANGE, {
      persona
    });
  }, [isRecording, persona]);

  // Track mode changes
  useEffect(() => {
    if (!isRecording || !sessionRef.current) return;
    
    addRecordingEvent(sessionRef.current, SESSION_EVENTS.MODE_CHANGE, {
      mode
    });
  }, [isRecording, mode]);

  // Track play/pause
  useEffect(() => {
    if (!isRecording || !sessionRef.current) return;
    
    addRecordingEvent(sessionRef.current, isPlaying ? SESSION_EVENTS.PLAY : SESSION_EVENTS.PAUSE, {});
  }, [isRecording, isPlaying]);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = () => {
    sessionRef.current = createRecordingSession({
      userId: user?.id,
      orgId: user?.organization_id,
      purpose,
      persona,
      mode,
      speed
    });

    addRecordingEvent(sessionRef.current, SESSION_EVENTS.START, {});
    addRecordingEvent(sessionRef.current, SESSION_EVENTS.STEP_ENTER, {
      stepIndex: currentStepIndex
    });

    prevStepRef.current = currentStepIndex;
    setRecordingTime(0);
    setIsRecording(true);
    toast.success('Recording started');
  };

  const stopRecording = () => {
    if (!sessionRef.current) return;

    addRecordingEvent(sessionRef.current, SESSION_EVENTS.END, {});
    const finalSession = finalizeRecording(sessionRef.current);
    
    setIsRecording(false);
    
    // Save session
    saveSession(finalSession);
  };

  const discardRecording = () => {
    sessionRef.current = null;
    setIsRecording(false);
    setRecordingTime(0);
    toast.info('Recording discarded');
  };

  const saveSession = async (session) => {
    try {
      // In a real implementation, this would POST to the backend
      // For now, we'll save to localStorage
      const savedSessions = JSON.parse(localStorage.getItem('granullo.guided.sessions') || '[]');
      savedSessions.push(session);
      localStorage.setItem('granullo.guided.sessions', JSON.stringify(savedSessions));
      
      toast.success(`Session saved (${formatDuration(session.metadata.totalDuration)})`);
      
      if (onSessionSaved) {
        onSessionSaved(session);
      }
    } catch (e) {
      toast.error('Failed to save session');
    }
  };

  if (!canRecord || !isOpen) return null;

  return (
    <div className={cn(
      "fixed bottom-24 left-1/2 -translate-x-1/2 z-50",
      "bg-card border border-border rounded-lg shadow-lg",
      "px-4 py-2",
      "flex items-center gap-3"
    )}>
      {!isRecording ? (
        <>
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger className="w-[120px] h-8 text-[11px]" data-testid="purpose-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SESSION_PURPOSES.SALES}>Sales</SelectItem>
              <SelectItem value={SESSION_PURPOSES.RBI}>RBI Demo</SelectItem>
              <SelectItem value={SESSION_PURPOSES.TRAINING}>Training</SelectItem>
              <SelectItem value={SESSION_PURPOSES.AUDIT}>Audit Trail</SelectItem>
              <SelectItem value={SESSION_PURPOSES.GENERAL}>General</SelectItem>
            </SelectContent>
          </Select>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={startRecording}
                  className="h-8 gap-1.5"
                  data-testid="start-recording-btn"
                >
                  <Circle className="h-3 w-3 fill-current" />
                  Record
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-[11px]">Start recording this guided session</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Circle className="h-3 w-3 fill-red-500 text-[var(--g-danger)] animate-pulse" />
            <span className="text-[12px] font-mono text-foreground">
              {formatDuration(recordingTime * 1000)}
            </span>
          </div>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stopRecording}
                  className="h-8 gap-1.5"
                  data-testid="stop-recording-btn"
                >
                  <Square className="h-3 w-3 fill-current" />
                  Stop
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-[11px]">Stop and save recording</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={discardRecording}
                  className="h-8 w-8 p-0 text-muted-foreground"
                  data-testid="discard-recording-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-[11px]">Discard recording</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
    </div>
  );
};

export default SessionRecordingControls;
